import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { ChevronDown, ChevronUp, LucideAngularModule, MapPin, Search, X } from 'lucide-angular';
import { LessonPlanService } from '../../services/lesson-plan.service';
import { MetadataApiService } from '../../../../../common/services/metadata-api.service';
import { AuthService } from '../../../../../common/auth/auth.service';
import {
  Grade,
  Tier1Subject,
  Tier2Subject,
  US_STATES,
  UsState,
  HIDDEN_GRADE_VALUES,
} from '../../../../../common/services/metadata.models';
import { THEME_COLOR } from '../../services/lesson-plan.models';

interface FilteredTier1Subject extends Tier1Subject {
  isRelevant: boolean;
}

interface FilteredTier2Subject extends Tier2Subject {
  isRelevant: boolean;
}

@Component({
  selector: 'app-step-input',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './step-input.html',
  styleUrl: './step-input.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepInputComponent implements OnInit {
  private readonly lessonPlanService = inject(LessonPlanService);
  private readonly metadataApiService = inject(MetadataApiService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  @Output() readonly next = new EventEmitter<void>();
  @ViewChild('stateCombobox') stateComboboxRef!: ElementRef<HTMLDivElement>;

  protected readonly themeColor = THEME_COLOR;
  protected readonly inputData$ = this.lessonPlanService.inputData$;
  protected readonly error$ = this.lessonPlanService.error$;
  protected readonly isAuthenticated$ = this.authService.isAuthenticated();

  /** Metadata from API */
  private readonly grades$$ = new BehaviorSubject<Grade[]>([]);
  protected readonly grades$ = this.grades$$.asObservable();

  /** Show more toggle for grades (K-5 are hidden initially) */
  private readonly showMoreGrades$$ = new BehaviorSubject<boolean>(false);
  protected readonly showMoreGrades$ = this.showMoreGrades$$.asObservable();

  /** Grades hidden by default (K=0, 1-5) */
  private readonly HIDDEN_GRADE_VALUES = HIDDEN_GRADE_VALUES;

  /** Visible grades (respecting show more toggle) */
  protected readonly visibleGrades$: Observable<Grade[]> = combineLatest([
    this.grades$,
    this.showMoreGrades$,
  ]).pipe(
    map(([grades, showMore]) => {
      if (showMore) {
        return grades;
      }
      // Only show grades 6+
      return grades.filter((g) => !this.HIDDEN_GRADE_VALUES.includes(g.value));
    }),
  );

  /** Check if there are hidden grades */
  protected readonly hasHiddenGrades$: Observable<boolean> = this.grades$.pipe(
    map((grades) => grades.some((g) => this.HIDDEN_GRADE_VALUES.includes(g.value))),
  );

  private readonly tier1Subjects$$ = new BehaviorSubject<Tier1Subject[]>([]);

  /** Show more toggles for tier1 and tier2 subjects */
  private readonly showMoreTier1$$ = new BehaviorSubject<boolean>(false);
  protected readonly showMoreTier1$ = this.showMoreTier1$$.asObservable();

  private readonly showMoreTier2$$ = new BehaviorSubject<boolean>(false);
  protected readonly showMoreTier2$ = this.showMoreTier2$$.asObservable();

  /** Filtered tier1 subjects based on selected grade */
  protected readonly filteredTier1Subjects$: Observable<FilteredTier1Subject[]> = combineLatest([
    this.tier1Subjects$$,
    this.inputData$,
  ]).pipe(
    map(([tier1Subjects, inputData]) => {
      const selectedGrade = inputData.grade;
      return tier1Subjects.map((tier1) => ({
        ...tier1,
        isRelevant:
          selectedGrade !== null &&
          tier1.subjects.some((s) => s.applicableGrades.includes(selectedGrade)),
      }));
    }),
  );

  /** Visible tier1 subjects (respecting show more toggle) */
  protected readonly visibleTier1Subjects$: Observable<FilteredTier1Subject[]> = combineLatest([
    this.filteredTier1Subjects$,
    this.showMoreTier1$,
  ]).pipe(
    map(([subjects, showMore]) => {
      if (showMore) {
        // Show relevant first, then non-relevant
        return [...subjects].sort((a, b) => (b.isRelevant ? 1 : 0) - (a.isRelevant ? 1 : 0));
      }
      // Only show relevant ones
      return subjects.filter((s) => s.isRelevant);
    }),
  );

  /** Check if there are hidden tier1 subjects */
  protected readonly hasHiddenTier1$: Observable<boolean> = this.filteredTier1Subjects$.pipe(
    map((subjects) => subjects.some((s) => !s.isRelevant)),
  );

  /** Filtered tier2 subjects based on selected grade and tier1 */
  protected readonly filteredTier2Subjects$: Observable<FilteredTier2Subject[]> = combineLatest([
    this.tier1Subjects$$,
    this.inputData$,
  ]).pipe(
    map(([tier1Subjects, inputData]) => {
      const selectedTier1 = tier1Subjects.find((t) => t.id === inputData.tier1SubjectId);
      if (!selectedTier1) {
        return [];
      }
      const selectedGrade = inputData.grade;
      return selectedTier1.subjects.map((subject) => ({
        ...subject,
        isRelevant:
          selectedGrade !== null && subject.applicableGrades.includes(selectedGrade),
      }));
    }),
  );

  /** Visible tier2 subjects (respecting show more toggle) */
  protected readonly visibleTier2Subjects$: Observable<FilteredTier2Subject[]> = combineLatest([
    this.filteredTier2Subjects$,
    this.showMoreTier2$,
  ]).pipe(
    map(([subjects, showMore]) => {
      if (showMore) {
        // Show relevant first, then non-relevant
        return [...subjects].sort((a, b) => (b.isRelevant ? 1 : 0) - (a.isRelevant ? 1 : 0));
      }
      // Only show relevant ones
      return subjects.filter((s) => s.isRelevant);
    }),
  );

  /** Check if there are hidden tier2 subjects */
  protected readonly hasHiddenTier2$: Observable<boolean> = this.filteredTier2Subjects$.pipe(
    map((subjects) => subjects.some((s) => !s.isRelevant)),
  );

  protected readonly icons = {
    ChevronDown,
    ChevronUp,
    Search,
    X,
    MapPin,
  };

  /** US State combobox state */
  protected readonly usStates = US_STATES;
  private readonly stateSearchText$$ = new BehaviorSubject<string>('');
  protected readonly stateSearchText$ = this.stateSearchText$$.asObservable();
  private readonly stateDropdownOpen$$ = new BehaviorSubject<boolean>(false);
  protected readonly stateDropdownOpen$ = this.stateDropdownOpen$$.asObservable();
  private readonly stateDropdownOpenUp$$ = new BehaviorSubject<boolean>(false);
  protected readonly stateDropdownOpenUp$ = this.stateDropdownOpenUp$$.asObservable();

  /** Filtered states based on search text */
  protected readonly filteredStates$: Observable<UsState[]> = this.stateSearchText$.pipe(
    map((searchText) => {
      if (!searchText.trim()) {
        return this.usStates;
      }
      const search = searchText.toLowerCase();
      return this.usStates.filter(
        (state) =>
          state.name.toLowerCase().includes(search) || state.code.toLowerCase().includes(search),
      );
    }),
  );

  /** Get selected state name for display */
  protected readonly selectedStateName$: Observable<string | null> = this.inputData$.pipe(
    map((data) => {
      if (!data.usState) return null;
      const state = this.usStates.find((s) => s.code === data.usState);
      return state?.name || null;
    }),
  );

  ngOnInit(): void {
    this.loadMetadata();
  }

  private loadMetadata(): void {
    this.metadataApiService
      .getMetadata()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((metadata) => {
        this.grades$$.next(metadata.grades);
        this.tier1Subjects$$.next(metadata.tier1Subjects);
      });
  }

  protected toggleShowMoreGrades(): void {
    this.showMoreGrades$$.next(!this.showMoreGrades$$.value);
  }

  protected updateGrade(value: number): void {
    // When grade changes, reset tier1 and tier2 selections if they become invalid
    const currentData = this.lessonPlanService.inputData;
    const tier1Subjects = this.tier1Subjects$$.value;

    // Check if current tier1 selection is still valid for the new grade
    let newTier1SubjectId = currentData.tier1SubjectId;
    let newTier2SubjectId = currentData.tier2SubjectId;

    if (newTier1SubjectId) {
      const tier1 = tier1Subjects.find((t) => t.id === newTier1SubjectId);
      const tier1HasValidSubjects = tier1?.subjects.some((s) => s.applicableGrades.includes(value));
      if (!tier1HasValidSubjects) {
        newTier1SubjectId = null;
        newTier2SubjectId = null;
      } else if (newTier2SubjectId) {
        // Check if tier2 is still valid
        const tier2 = tier1?.subjects.find((s) => s.id === newTier2SubjectId);
        if (!tier2?.applicableGrades.includes(value)) {
          newTier2SubjectId = null;
        }
      }
    }

    this.lessonPlanService.updateInputData({
      grade: value,
      tier1SubjectId: newTier1SubjectId,
      tier2SubjectId: newTier2SubjectId,
    });

    // Reset show more states when grade changes
    this.showMoreTier1$$.next(false);
    this.showMoreTier2$$.next(false);
  }

  protected updateTier1Subject(id: string): void {
    const currentData = this.lessonPlanService.inputData;
    // Toggle off if already selected
    if (currentData.tier1SubjectId === id) {
      this.lessonPlanService.updateInputData({ tier1SubjectId: null, tier2SubjectId: null });
    } else {
      // Clear tier2 when tier1 changes
      this.lessonPlanService.updateInputData({ tier1SubjectId: id, tier2SubjectId: null });
    }
    this.showMoreTier2$$.next(false);
  }

  protected updateTier2Subject(id: string): void {
    const currentData = this.lessonPlanService.inputData;
    // Toggle off if already selected
    if (currentData.tier2SubjectId === id) {
      this.lessonPlanService.updateInputData({ tier2SubjectId: null });
    } else {
      this.lessonPlanService.updateInputData({ tier2SubjectId: id });
    }
  }

  protected toggleShowMoreTier1(): void {
    this.showMoreTier1$$.next(!this.showMoreTier1$$.value);
  }

  protected toggleShowMoreTier2(): void {
    this.showMoreTier2$$.next(!this.showMoreTier2$$.value);
  }

  /** US State combobox methods */
  protected onStateSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.stateSearchText$$.next(input.value);
    if (!this.stateDropdownOpen$$.value) {
      this.stateDropdownOpen$$.next(true);
    }
  }

  protected openStateDropdown(): void {
    // Calculate if dropdown should open upward based on available space
    if (this.stateComboboxRef) {
      const rect = this.stateComboboxRef.nativeElement.getBoundingClientRect();
      const dropdownHeight = 240; // max-h-60 = 15rem = 240px
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Open upward if not enough space below but enough space above
      const shouldOpenUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      this.stateDropdownOpenUp$$.next(shouldOpenUp);
    }
    this.stateDropdownOpen$$.next(true);
  }

  protected closeStateDropdown(): void {
    // Small delay to allow click events on dropdown items to fire
    setTimeout(() => {
      this.stateDropdownOpen$$.next(false);
      this.stateSearchText$$.next('');
    }, 150);
  }

  protected selectState(state: UsState): void {
    this.lessonPlanService.updateInputData({ usState: state.code });
    this.stateDropdownOpen$$.next(false);
    this.stateSearchText$$.next('');
  }

  protected clearState(event: Event): void {
    event.stopPropagation();
    this.lessonPlanService.updateInputData({ usState: null });
    this.stateSearchText$$.next('');
  }

  protected onContinue(): void {
    // Create lesson plan and navigate to settings step
    this.lessonPlanService
      .createLessonPlanOnly()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // Navigate to the lesson plan URL with ID and set to step 2 (settings)
          const lessonPlanId = this.lessonPlanService.lessonPlanId;
          if (lessonPlanId) {
            // Explicitly set to step 2 (settings), then navigate to URL with ID
            this.lessonPlanService.setStep(2);
            this.router.navigate(['/lesson-plan', lessonPlanId]).then(() => {
              // Scroll to top after navigation completes
              this.scrollToTopWithRepaint();
            });
          } else {
            // Fallback: just set to step 2
            this.lessonPlanService.setStep(2);
            this.scrollToTopWithRepaint();
          }
        },
        error: () => {
          // Error is already handled by the service
        },
      });
  }

  protected onNext(): void {
    this.next.emit();
  }

  protected onExit(): void {
    this.router.navigate(['/dashboard']);
  }

  /**
   * Scroll to top with Safari mobile repaint fix to prevent white screen issues.
   */
  private scrollToTopWithRepaint(): void {
    // Use instant scroll on mobile Safari to avoid rendering issues
    const isSafariMobile =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.userAgent.includes('Safari') && 'ontouchstart' in window);

    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: isSafariMobile ? 'instant' : 'smooth',
      });

      // Force repaint on Safari to prevent white screen bug
      if (isSafariMobile) {
        requestAnimationFrame(() => {
          document.body.style.transform = 'translateZ(0)';
          requestAnimationFrame(() => {
            document.body.style.transform = '';
          });
        });
      }
    }, 0);
  }
}
